// =================================================================================================
// MetricRegistry — every metric defined ONCE (the "fact over subjective, explained" spine).
//
// Each entry is a `MetricDef` that EXTENDS the contract's `MetricMeta` (key/label/unit/
// directionality/isHealthScore/datasetMetricKey) and adds the FE-only presentation: a human formula
// string, optional threshold bands, an optional RAG rule, and the plain-English tooltip copy that
// `MetricTooltip` shows. Keyed by `MetricKey` — the contract's closed key space is the source of
// truth, so a metric exists here iff it exists there.
//
// COLOR LAW: `ragRule` is only ever populated for `isHealthScore` metrics. A real-unit fact (memory
// MB, USD, voltage) may carry threshold bands for context, but never a RAG color. The `metricRag()`
// helper enforces this at the call site — it returns null for any non-health-score metric.
// =================================================================================================

import type { MetricKey, MetricMeta, RiskTier } from "@busright/data-contract";

// A threshold band: values where `value >= from` (and, if set, `< to`) fall in this band. Used for
// context labels ("healthy" / "elevated") and, for health scores ONLY, a RAG tier.
export interface MetricThreshold {
  readonly from: number;
  readonly to?: number;
  readonly label: string;
}

// FE registry entry. Extends the shared contract slice with presentation-only fields.
export interface MetricDef extends MetricMeta {
  // One-line human formula, e.g. "available_ram_mb / total_ram_mb × 100". Shown in the tooltip.
  readonly formula: string;
  // Plain-English definition the tooltip leads with — what the number MEANS, in one or two sentences.
  readonly tooltip: string;
  // Optional context bands (ordered low → high). For health scores these double as the RAG mapping.
  readonly thresholds?: readonly MetricThreshold[];
  // RAG mapping for HEALTH SCORES ONLY (isHealthScore=true). Maps a 0..100 score to a RiskTier.
  // Undefined for every fact metric — enforces "RAG = health only".
  readonly ragRule?: (value: number) => RiskTier;
}

// Standard 0..100 composite/subscore RAG mapping: <60 critical, <80 warn, else ok. Reused by every
// health.* entry so the bands are consistent across the registry.
const healthRag = (value: number): RiskTier => {
  if (value < 60) {
    return "CRITICAL";
  }
  if (value < 80) {
    return "WARN";
  }
  return "OK";
};

const HEALTH_BANDS: readonly MetricThreshold[] = [
  { from: 0, to: 60, label: "Critical" },
  { from: 60, to: 80, label: "Warn" },
  { from: 80, label: "OK" },
] as const;

// Build a health-score def with the shared RAG rule + bands, given the FE-only copy.
const healthDef = (key: MetricKey, label: string, formula: string, tooltip: string): MetricDef => ({
  key,
  label,
  unit: "score",
  directionality: "lowerIsWorse",
  isHealthScore: true,
  datasetMetricKey: key,
  formula,
  tooltip,
  thresholds: HEALTH_BANDS,
  ragRule: healthRag,
});

// The registry. Partial over MetricKey — we seed the metrics used by Lifecycle, Fleet Health,
// Finance, and Telemetry (the reference + the three proof surfaces). The exhaustive `METRIC_KEYS`
// array stays the contract's responsibility; surfaces look up by key and tolerate a miss gracefully.
const REGISTRY: Partial<Record<MetricKey, MetricDef>> = {
  // ── Health composite + subscores (0–100; RAG applies) ──────────────────────────────────────────
  "health.composite": healthDef(
    "health.composite",
    "Composite health",
    "weighted mean of the 5 subscores (gps, connectivity, stability, battery, memory)",
    "A 0–100 roll-up of a device's five health subscores. Lower is worse. Below 60 is CRITICAL, 60–80 is WARN. This is a score, not a real unit — drill in to see the underlying facts.",
  ),
  "health.gps": healthDef(
    "health.gps",
    "GPS subscore",
    "0–100 from GPS fix quality + accuracy over the window",
    "How reliably a device gets an accurate GPS fix, as a 0–100 score. Lower means more dropped or low-accuracy fixes.",
  ),
  "health.connectivity": healthDef(
    "health.connectivity",
    "Connectivity subscore",
    "0–100 from cellular signal + reporting cadence",
    "How consistently a device stays online and reports, as a 0–100 score. Lower means weak signal or long reporting gaps.",
  ),
  "health.stability": healthDef(
    "health.stability",
    "Stability subscore",
    "0–100 from crash / reboot / app-uptime signals",
    "How stable a device's software is, as a 0–100 score. Lower means more crashes or reboots.",
  ),
  "health.battery": healthDef(
    "health.battery",
    "Battery subscore",
    "0–100 from battery temperature, voltage, and charge behavior",
    "Battery health as a 0–100 score, derived from temperature, voltage, and charging behavior. Lower means a battery running hot or undervolted.",
  ),
  "health.memory": healthDef(
    "health.memory",
    "Memory subscore",
    "0–100 from available-RAM headroom",
    "Memory headroom as a 0–100 score. This is the SCORE; for the real fact see Memory used/total (MB), which is what most operators want.",
  ),

  // ── Battery telemetry (real units; FACT, never RAG) ──────────────────────────────────────────
  "battery.temperatureC": {
    key: "battery.temperatureC",
    label: "Battery temperature",
    unit: "celsius",
    directionality: "higherIsWorse",
    isHealthScore: false,
    datasetMetricKey: "battery.temperatureC",
    formula: "latest esper_device_status.battery_temperature (°C)",
    tooltip:
      "The device battery's latest reported temperature in °C. A real reading, not a score. Sustained high temperatures shorten battery life.",
    thresholds: [
      { from: 0, to: 40, label: "Normal" },
      { from: 40, label: "Elevated" },
    ],
  },
  "battery.voltageV": {
    key: "battery.voltageV",
    label: "Battery voltage",
    unit: "volt",
    directionality: "lowerIsWorse",
    isHealthScore: false,
    datasetMetricKey: "battery.voltageV",
    formula: "latest esper_device_status.battery_voltage (V)",
    tooltip:
      "The battery's latest voltage in volts. Lower-than-expected voltage across a district's fleet can flag underperforming charging hardware.",
  },
  "battery.levelPct": {
    key: "battery.levelPct",
    label: "Battery level",
    unit: "percent",
    directionality: "lowerIsWorse",
    isHealthScore: false,
    datasetMetricKey: "battery.levelPct",
    formula: "latest esper_device_status.battery_level (%)",
    tooltip:
      "The battery's latest charge level as a percentage. A real reading, not a health score.",
  },

  // ── CPU telemetry ────────────────────────────────────────────────────────────────────────────
  "cpu.tempMaxC": {
    key: "cpu.tempMaxC",
    label: "CPU temperature (max)",
    unit: "celsius",
    directionality: "higherIsWorse",
    isHealthScore: false,
    datasetMetricKey: "cpu.tempMaxC",
    formula: "max CPU sensor temperature on the latest reading (°C)",
    tooltip:
      "The hottest CPU sensor reading on the device's latest report, in °C. A real reading — sustained highs can indicate thermal stress.",
  },
  "cpu.usagePct": {
    key: "cpu.usagePct",
    label: "CPU usage",
    unit: "percent",
    directionality: "higherIsWorse",
    isHealthScore: false,
    datasetMetricKey: "cpu.usagePct",
    formula: "latest CPU utilization (%)",
    tooltip: "The device's latest CPU utilization as a percentage. A real reading, not a score.",
  },

  // ── Memory (FACT: MB — never a 0–100 score) ────────────────────────────────────────────────────
  "memory.usedMb": {
    key: "memory.usedMb",
    label: "Memory used",
    unit: "mb",
    directionality: "higherIsWorse",
    isHealthScore: false,
    datasetMetricKey: "memory.usedMb",
    formula: "total_ram_mb − available_ram_mb (MB)",
    tooltip:
      "RAM in use, in megabytes — a real fact, NOT a 0–100 score. Read it against total RAM to see real headroom (this fixes the 'memory 66 means nothing' problem).",
  },
  "memory.totalMb": {
    key: "memory.totalMb",
    label: "Memory total",
    unit: "mb",
    directionality: "neutral",
    isHealthScore: false,
    datasetMetricKey: "memory.totalMb",
    formula:
      "esper_device_snapshot.total_ram_mb, or model default (TA5=4096, TA3=2048) when absent",
    tooltip:
      "Total installed RAM in megabytes. From the device snapshot when available; otherwise inferred from the hardware model and flagged as inferred.",
  },
  "memory.freeMb": {
    key: "memory.freeMb",
    label: "Memory free",
    unit: "mb",
    directionality: "lowerIsWorse",
    isHealthScore: false,
    datasetMetricKey: "memory.freeMb",
    formula: "esper_device_status.available_ram_mb (MB)",
    tooltip:
      "Free RAM in megabytes — a real reading. Low free memory across a fleet is a concrete signal an engineer can act on.",
  },
  "memory.usedPct": {
    key: "memory.usedPct",
    label: "Memory used %",
    unit: "percent",
    directionality: "higherIsWorse",
    isHealthScore: false,
    datasetMetricKey: "memory.usedMb",
    formula: "used_mb / total_ram_mb × 100",
    tooltip:
      "RAM in use as a percentage of total — a DERIVED FACT ratio, not a health score. Drill in for the underlying used/total MB.",
  },

  // ── Connectivity / signal ────────────────────────────────────────────────────────────────────
  "signal.cellularDb": {
    key: "signal.cellularDb",
    label: "Cellular signal",
    unit: "db",
    directionality: "lowerIsWorse",
    isHealthScore: false,
    datasetMetricKey: "signal.cellularDb",
    formula: "latest cellular signal strength (dBm)",
    tooltip:
      "Latest cellular signal strength in dBm (closer to 0 is stronger). A real reading, not a score.",
  },

  // ── Lifecycle / age ──────────────────────────────────────────────────────────────────────────
  "device.ageDays": {
    key: "device.ageDays",
    label: "Device age",
    unit: "days",
    directionality: "higherIsWorse",
    isHealthScore: false,
    datasetMetricKey: "device.ageDays",
    formula: "today − esper_fleet_roster.provisioned_on (days)",
    tooltip:
      "How long ago a device was first provisioned, in days. The real intake history that ages into cohorts; older devices skew toward higher risk.",
  },
  "device.provisionedOn": {
    key: "device.provisionedOn",
    label: "Provisioned on",
    unit: "date",
    directionality: "neutral",
    isHealthScore: false,
    datasetMetricKey: "device.provisionedOn",
    formula: "esper_fleet_roster.provisioned_on",
    tooltip:
      "The date a device was first provisioned into the fleet. The intake event each cohort is built from.",
  },
  "device.replacementRatePct": {
    key: "device.replacementRatePct",
    label: "Replacement rate",
    unit: "percent",
    directionality: "higherIsWorse",
    isHealthScore: false,
    datasetMetricKey: "device.replacementRatePct",
    formula: "replacements ÷ installed base × 100 (per slice)",
    tooltip:
      "Share of a fleet slice replaced over the window — replacements divided by installed base. A real rate, not a health score.",
  },

  // ── Finance / AR (USD; BusRight's own AR posture, never customer revenue) ───────────────────────
  "finance.arOutstandingUsd": {
    key: "finance.arOutstandingUsd",
    label: "Outstanding AR",
    unit: "usd",
    directionality: "higherIsWorse",
    isHealthScore: false,
    datasetMetricKey: "finance.arOutstandingUsd",
    formula: "Σ open-invoice balances in the month (USD)",
    tooltip:
      "BusRight's open accounts-receivable balance for the month, in USD — invoices billed but not yet paid. This is AR posture, NOT customer revenue.",
  },
  "finance.arPaidUsd": {
    key: "finance.arPaidUsd",
    label: "Paid",
    unit: "usd",
    directionality: "lowerIsWorse",
    isHealthScore: false,
    datasetMetricKey: "finance.arPaidUsd",
    formula: "Σ closed/paid invoice amounts in the month (USD)",
    tooltip:
      "Invoiced amounts paid (closed) in the month, in USD. Plotted against outstanding so finance can watch the gap close.",
  },
  "finance.arBalanceUsd": {
    key: "finance.arBalanceUsd",
    label: "AR balance",
    unit: "usd",
    directionality: "higherIsWorse",
    isHealthScore: false,
    datasetMetricKey: "finance.arBalanceUsd",
    formula: "current AR balance from stg_e2_credits (USD)",
    tooltip:
      "BusRight's current accounts-receivable balance in USD. AR posture, not customer revenue.",
  },
  "finance.invoiceCount": {
    key: "finance.invoiceCount",
    label: "Invoices",
    unit: "count",
    directionality: "neutral",
    isHealthScore: false,
    datasetMetricKey: "finance.invoiceCount",
    formula: "count of invoices in the month",
    tooltip:
      "Number of invoices in the month. A volume count — drill in for the individual invoices.",
  },
  "finance.latePaymentUsd": {
    key: "finance.latePaymentUsd",
    label: "Late payments",
    unit: "usd",
    directionality: "higherIsWorse",
    isHealthScore: false,
    datasetMetricKey: "finance.latePaymentUsd",
    formula: "Σ outstanding past due_date (USD)",
    tooltip:
      "Outstanding AR that is past its due date, in USD — the late-payment overlay on the MoM graph. A money fact, never RAG-colored.",
  },
  "finance.creditRemainingUsd": {
    key: "finance.creditRemainingUsd",
    label: "Credit remaining",
    unit: "usd",
    directionality: "lowerIsWorse",
    isHealthScore: false,
    datasetMetricKey: "finance.creditRemainingUsd",
    formula: "credit_limit − current AR balance (USD)",
    tooltip: "Remaining credit headroom in USD. At or below zero is a credit-hold condition.",
  },

  // ── RMA / returns (end-of-life signal) ───────────────────────────────────────────────────────
  "rma.openCount": {
    key: "rma.openCount",
    label: "Open RMAs",
    unit: "count",
    directionality: "higherIsWorse",
    isHealthScore: false,
    datasetMetricKey: "rma.openCount",
    formula: "count of RMAs with no close date",
    tooltip:
      "Return-merchandise authorizations still open. A count — drill in for the individual RMAs.",
  },
  "rma.labelSentNotReturnedCount": {
    key: "rma.labelSentNotReturnedCount",
    label: "Label sent, not returned",
    unit: "count",
    directionality: "higherIsWorse",
    isHealthScore: false,
    datasetMetricKey: "rma.labelSentNotReturnedCount",
    formula: "INFERRED: outbound return-label orders with no matching return after N business days",
    tooltip:
      "Return labels we INFER were sent (an outbound order matched by date + recipient + SKU) with no return recorded. Inferred, not an exact record — each row carries a confidence.",
  },
  "rma.agingDays": {
    key: "rma.agingDays",
    label: "RMA age",
    unit: "days",
    directionality: "higherIsWorse",
    isHealthScore: false,
    datasetMetricKey: "rma.agingDays",
    formula: "days between an open RMA's open date and today",
    tooltip: "How long an open RMA has been outstanding, in days. Higher means it has aged longer.",
  },
  "rma.resolvedCount": {
    key: "rma.resolvedCount",
    label: "Resolved RMAs",
    unit: "count",
    directionality: "neutral",
    isHealthScore: false,
    datasetMetricKey: "rma.resolvedCount",
    formula: "count of RMAs with a close/resolution date",
    tooltip: "Return-merchandise authorizations that have been closed/resolved. A volume count.",
  },

  // ── Fleet / coverage rollups ─────────────────────────────────────────────────────────────────
  "fleet.deviceCount": {
    key: "fleet.deviceCount",
    label: "Devices",
    unit: "count",
    directionality: "neutral",
    isHealthScore: false,
    datasetMetricKey: "fleet.deviceCount",
    formula: "distinct devices in scope",
    tooltip: "Number of distinct devices in the current scope. A volume count.",
  },
  "fleet.criticalSharePct": {
    key: "fleet.criticalSharePct",
    label: "% critical",
    unit: "percent",
    directionality: "higherIsWorse",
    isHealthScore: false,
    datasetMetricKey: "health.composite",
    formula: "CRITICAL devices ÷ scored devices × 100",
    tooltip:
      "Share of scored devices in the CRITICAL risk tier. A percentage derived from health risk tiers; drill in to the device list behind it.",
  },
  "fleet.accountMatchRatePct": {
    key: "fleet.accountMatchRatePct",
    label: "Account match rate",
    unit: "percent",
    directionality: "lowerIsWorse",
    isHealthScore: false,
    datasetMetricKey: "fleet.accountMatchRatePct",
    formula: "recipients resolved to a district ÷ total recipients × 100",
    tooltip:
      "Share of supply-chain recipients we could resolve to a warehouse district. A data-coverage percentage, not a health score.",
  },

  // ── Supply volume facts (never RAG — volume, not health) ─────────────────────────────────────
  "supply.orderQty": {
    key: "supply.orderQty",
    label: "Order quantity",
    unit: "count",
    directionality: "neutral",
    isHealthScore: false,
    datasetMetricKey: "supply.orderQty",
    formula: "sum of parsed (N) unit quantities on the order's SKU lines",
    tooltip:
      "Units on an order, summed from the parsed (N) quantities in its description SKU lines. A volume count, not a health score.",
  },
  "supply.inventoryQtyAvailable": {
    key: "supply.inventoryQtyAvailable",
    label: "Inventory available",
    unit: "count",
    directionality: "neutral",
    isHealthScore: false,
    datasetMetricKey: "supply.inventoryQtyAvailable",
    formula: "available on-hand quantity from the E2 inventory feed",
    tooltip:
      "On-hand units available in the warehouse inventory feed. A volume count, not a health score.",
  },
};

// Look up a metric definition. Returns undefined for keys not yet seeded so callers degrade
// gracefully (render the raw label / no tooltip) rather than throwing.
export const getMetricDef = (key: MetricKey): MetricDef | undefined => REGISTRY[key];

// Whether a metric is currently registered (has FE copy + config).
export const hasMetricDef = (key: MetricKey): boolean => key in REGISTRY;

// Resolve a metric's RAG tier for a value — ONLY for health scores with a ragRule. Returns null for
// every fact metric, enforcing "RAG = health only" at the call site regardless of caller intent.
export const metricRag = (key: MetricKey, value: number): RiskTier | null => {
  const def = REGISTRY[key];
  if (!def || !def.isHealthScore || !def.ragRule) {
    return null;
  }
  return def.ragRule(value);
};
